import { Component, Input, ChangeDetectionStrategy, signal, ElementRef, ViewChild, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TooltipModule } from '../../../../../shared/ui-kit/components/tooltip/tooltip.module';
import { XPlaceholderRowComponent } from '../../../../../shared/x-kit/components/x-placeholder-row/x-placeholder-row.component';
import { XPlaceholderFixedLoaderComponent } from '../../../../../shared/x-kit/components/x-placeholder-row/x-placeholder-fixed-loader/x-placeholder-fixed-loader.component';
import { XPlaceholderFlexibleLoaderComponent } from '../../../../../shared/x-kit/components/x-placeholder-row/x-placeholder-flexible-loader/x-placeholder-flexible-loader.component';

export type WidgetSize = 'compact' | 'medium' | 'full';

export interface ReliabilityFactor {
  title: string;
  description: string;
  status: 'positive' | 'negative' | 'neutral';
}

export interface ReliabilityData {
  title: string;
  subtitle: string;
  score: {
    value: number;
    maxValue: number;
    level: 'high' | 'medium' | 'low' | 'very-low';
  };
  factors: ReliabilityFactor[];
}

const VISIBLE_FACTORS = 3;

@Component({
  selector: 'app-reliability-card',
  standalone: true,
  imports: [CommonModule, TranslateModule, TooltipModule, XPlaceholderRowComponent, XPlaceholderFixedLoaderComponent, XPlaceholderFlexibleLoaderComponent],
  template: `
    <div class="reliability-card" [class]="'size-' + currentSize()">
      <div class="card-header">
        <div class="title-block">
          <span class="title-text">{{ getTitle() }}</span>
          <button class="info-button" type="button" [tooltip]="'reliability_widget_tooltip' | translate" [tooltipPosition]="'top'" [attr.aria-label]="'reliability_widget_aria_info' | translate" *ngIf="currentSize() !== 'compact'">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <circle cx="8" cy="8" r="7" stroke="#9CA3AF" stroke-width="1.5"/>
              <path d="M8 11V8M8 5h.01" stroke="#9CA3AF" stroke-width="1.5" stroke-linecap="round"/>
          </svg>
          </button>
      </div>
        <div class="toolbar-menu" #menuContainer>
          <button class="toolbar-button" type="button" (click)="toggleMenu()" [attr.aria-label]="'reliability_widget_size_menu' | translate">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <circle cx="8" cy="3" r="1.5" fill="#9CA3AF"/>
              <circle cx="8" cy="8" r="1.5" fill="#9CA3AF"/>
              <circle cx="8" cy="13" r="1.5" fill="#9CA3AF"/>
            </svg>
          </button>
          <div class="size-menu" *ngIf="isMenuOpen()" #sizeMenu>
            <button class="size-option" [class.active]="currentSize() === 'compact'" (click)="setSize('compact')">
              <span class="size-icon">◼</span>
              <span>{{ 'reliability_widget_size_compact' | translate }}</span>
            </button>
            <button class="size-option" [class.active]="currentSize() === 'medium'" (click)="setSize('medium')">
              <span class="size-icon">▬</span>
              <span>{{ 'reliability_widget_size_medium' | translate }}</span>
            </button>
            <button class="size-option" [class.active]="currentSize() === 'full'" (click)="setSize('full')">
              <span class="size-icon">▮</span>
              <span>{{ 'reliability_widget_size_full' | translate }}</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Skeleton Placeholder -->
      <div class="skeleton-placeholder" *ngIf="isLoading || !data">
        <app-x-placeholder-row>
          <app-x-placeholder-fixed-loader [width]="'100px'" [height]="'24px'"></app-x-placeholder-fixed-loader>
          <app-x-placeholder-flexible-loader [grow]="1"></app-x-placeholder-flexible-loader>
        </app-x-placeholder-row>
        
        <app-x-placeholder-row>
          <app-x-placeholder-fixed-loader [width]="'80px'" [height]="'20px'"></app-x-placeholder-fixed-loader>
          <app-x-placeholder-flexible-loader [grow]="1"></app-x-placeholder-flexible-loader>
        </app-x-placeholder-row>
        
        <app-x-placeholder-row>
          <app-x-placeholder-fixed-loader [width]="'60px'" [height]="'20px'"></app-x-placeholder-fixed-loader>
          <app-x-placeholder-flexible-loader [grow]="1"></app-x-placeholder-flexible-loader>
        </app-x-placeholder-row>
      </div>

      <!-- Content when loaded -->
      <ng-container *ngIf="!isLoading && data">
      <!-- COMPACT SIZE -->
      <div class="compact-content" *ngIf="currentSize() === 'compact'">
        <div class="compact-status">
          {{ getEstimateStatus() | translate }}
        </div>
        </div>

      <!-- MEDIUM SIZE -->
      <ng-container *ngIf="currentSize() === 'medium'">
        <div class="score-block medium-layout">
          <div class="estimate-status">
            {{ getEstimateStatus() | translate }}
          </div>
        </div>

        <div class="factors-list-medium">
          <div class="factor-item" *ngFor="let factor of visibleFactors">
            <span class="bullet">•</span>
            <span class="factor-text">{{ factor.title }}</span>
        </div>
      </div>
      </ng-container>

      <!-- FULL SIZE -->
      <ng-container *ngIf="currentSize() === 'full'">
        <div class="score-block">
          <div class="estimate-status">
            {{ getEstimateStatus() | translate }}
          </div>
        </div>

        <!-- <div class="subtitle-text">{{ data.subtitle }}</div> -->

        <div class="factors-list">
          <div
            class="factor-card"
            *ngFor="let factor of visibleFactors"
            [class]="'factor-' + getFactorDisplayStatus()">
            <div class="factor-icon">
              <svg *ngIf="getFactorDisplayStatus() === 'positive'" width="16" height="16" viewBox="0 0 16 16" fill="none">
                <circle cx="8" cy="8" r="7.25" stroke="#9CA3AF" stroke-width="1.5"/>
                <path d="M5.5 8.25L7.25 10L10.5 6.5" stroke="#9CA3AF" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              <svg *ngIf="getFactorDisplayStatus() === 'negative'" width="16" height="16" viewBox="0 0 16 16" fill="none">
                <circle cx="8" cy="8" r="7.25" stroke="#9CA3AF" stroke-width="1.5"/>
                <path d="M5.5 5.5L10.5 10.5M10.5 5.5L5.5 10.5" stroke="#9CA3AF" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              <svg *ngIf="getFactorDisplayStatus() === 'neutral'" width="16" height="16" viewBox="0 0 16 16" fill="none">
                <circle cx="8" cy="8" r="7.25" stroke="#9CA3AF" stroke-width="1.5"/>
                <path d="M8 11V8M8 5h.01" stroke="#9CA3AF" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
            <div>
            <div class="factor-title">{{ factor.title }}</div>
            <div class="factor-description">{{ factor.description }}</div>
          </div>
        </div>
      </div>
      </ng-container>
      </ng-container>
    </div>
  `,
  styleUrl: './reliability-card.component.less',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReliabilityCardComponent implements OnDestroy {
  @Input() data: ReliabilityData | null = null;
  @Input() isLoading: boolean = false;

  @ViewChild('menuContainer', { static: false }) menuContainer?: ElementRef;

  currentSize = signal<WidgetSize>('full');
  isMenuOpen = signal<boolean>(false);
  private clickOutsideHandler?: (event: MouseEvent) => void;

  constructor(private translate: TranslateService, private elementRef: ElementRef) {}

  ngOnDestroy() {
    this.removeClickOutsideListener();
  }

  private removeClickOutsideListener() {
    if (this.clickOutsideHandler) {
      document.removeEventListener('click', this.clickOutsideHandler, true);
      this.clickOutsideHandler = undefined;
    }
  }

  private addClickOutsideListener() {
    this.removeClickOutsideListener();
    
    this.clickOutsideHandler = (event: MouseEvent) => {
      if (!this.isMenuOpen() || !this.menuContainer) {
        return;
      }

      const target = event.target as Node;
      const menuElement = this.menuContainer.nativeElement;

      // Check if click is outside the menu container
      if (!menuElement.contains(target)) {
        this.isMenuOpen.set(false);
        this.removeClickOutsideListener();
      }
    };

    // Use setTimeout to avoid immediate close on the click that opened the menu
    setTimeout(() => {
      document.addEventListener('click', this.clickOutsideHandler!, true);
    }, 0);
  }

  toggleMenu() {
    this.isMenuOpen.update(v => {
      const newValue = !v;
      if (newValue) {
        // Add listener when menu opens
        this.addClickOutsideListener();
      } else {
        // Remove listener when menu closes
        this.removeClickOutsideListener();
      }
      return newValue;
    });
  }

  setSize(size: WidgetSize) {
    this.currentSize.set(size);
    this.isMenuOpen.set(false);
    this.removeClickOutsideListener();
  }

  getTitle(): string {
    if (this.currentSize() === 'compact') {
      return this.translate.instant('reliability_widget_title_short');
    }
    return this.translate.instant('reliability_widget_title');
  }

  get visibleFactors(): ReliabilityFactor[] {
    if (!this.data?.factors) {
      return [];
    }
    return this.data.factors.slice(0, VISIBLE_FACTORS);
  }

  get additionalReasonsCount(): number {
    if (!this.data?.factors) {
      return 0;
    }
    return Math.max(this.data.factors.length - VISIBLE_FACTORS, 0);
  }

  get additionalReasonsLabel(): string {
    return this.translate.instant(
      this.additionalReasonsCount === 1 
        ? 'reliability_widget_reason_singular' 
        : 'reliability_widget_reason_plural'
    );
  }

  getScoreLabel(level: string): string {
    const key = `reliability_widget_score_${level}`;
    const translated = this.translate.instant(key);
    // Если перевод не найден, возвращаем оригинальный level
    return translated !== key ? translated : level;
  }

  getEstimateStatus(): string {
    if (!this.data?.score?.value) {
      return '';
    }
    const value = this.data.score.value;
    if (value >= 70) {
      return 'reliability_widget_status_confirmed';
    }
    if (value >= 50) {
      return 'reliability_widget_status_market_supported';
    }
    if (value >= 30) {
      return 'reliability_widget_status_indicative';
    }
    return 'reliability_widget_status_exploratory';
  }

  /**
   * Определяет визуальный статус фактора на основе общего уровня надежности (score.level),
   * а не на основе собственного статуса фактора. Это обеспечивает согласованность:
   * если лейбл красный (low/very-low), то все факторы должны быть красными с крестиками.
   */
  getFactorDisplayStatus(): 'positive' | 'negative' | 'neutral' {
    if (!this.data?.score?.level) {
      return 'neutral';
    }

    const level = this.data.score.level;
    
    // Если уровень высокий - все факторы зеленые с галочками
    if (level === 'high') {
      return 'positive';
    }
    
    // Если уровень низкий или очень низкий - все факторы красные с крестиками
    if (level === 'low' || level === 'very-low') {
      return 'negative';
    }
    
    // Если уровень средний - все факторы нейтральные (желтые)
    return 'neutral';
  }
}
